import React, { useEffect, useState } from 'react';
import { LayoutDashboard, Phone, Clock, PieChart, ArrowUp, ArrowDown } from 'lucide-react';
import { dbService, type CallSummary, type AgentInfo } from '../lib/database';
import { PieChart as RePieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

interface DashboardProps {
  userEmail: string;
}

interface AgentCallStats {
  agent_id: string;
  display_name: string;
  total_calls: number;
}

interface CallStatusStats {
  name: string;
  value: number;
}

// Helper function to parse PostgreSQL interval to seconds
function parseInterval(interval: string): number {
  // Handle empty or invalid intervals
  if (!interval) return 0;

  let totalSeconds = 0;

  // Match hours, minutes, seconds, and optionally microseconds
  const matches = interval.match(/(?:(\d+):)?(\d+):(\d+)(?:\.(\d+))?/);
  if (matches) {
    const [, hours = '0', minutes, seconds] = matches;
    totalSeconds = (parseInt(hours) * 3600) + (parseInt(minutes) * 60) + parseInt(seconds);
  }

  return totalSeconds;
}

// Helper function to format seconds to HH:MM:SS
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    remainingSeconds.toString().padStart(2, '0')
  ].join(':');
}

export function Dashboard({ userEmail }: DashboardProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentStats, setAgentStats] = useState<AgentCallStats[]>([]);
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [statusStats, setStatusStats] = useState<CallStatusStats[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof AgentCallStats;
    direction: 'asc' | 'desc';
  }>({ key: 'total_calls', direction: 'desc' });

  // Define colors for call statuses
  const STATUS_COLORS = {
    Success: '#10B981', // Green
    Failed: '#EF4444'   // Red
  };

  const handleSort = (key: keyof AgentCallStats) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedAgentStats = [...agentStats].sort((a, b) => {
    if (sortConfig.direction === 'asc') {
      return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
    }
    return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const profileData = await dbService.getProfileByEmail(userEmail);
        if (!profileData) throw new Error('Profile not found');

        const agentDisplayNames = new Map(
          (profileData.agents_info || []).map((agent: AgentInfo) => [
            agent.agent_id,
            agent.display_name
          ])
        );

        let calls: CallSummary[];
        if (profileData.role === 'user' && profileData.agents_info) {
          const agentIds = profileData.agents_info.map((agent: AgentInfo) => agent.agent_id);
          calls = await dbService.getCallSummariesByAgents(agentIds);
        } else {
          calls = await dbService.getAllCallSummaries();
        }

        // Calculate agent stats with display names from the profile's agents_info
        const agentCallsMap = new Map<string, { display_name: string; total_calls: number }>();
        
        calls.forEach(call => {
          const displayName = agentDisplayNames.get(call.agent_id) || `Agent ${call.agent_id.slice(0, 8)}`;
          if (!agentCallsMap.has(call.agent_id)) {
            agentCallsMap.set(call.agent_id, {
              display_name: displayName,
              total_calls: 1
            });
          } else {
            const current = agentCallsMap.get(call.agent_id)!;
            agentCallsMap.set(call.agent_id, {
              ...current,
              total_calls: current.total_calls + 1
            });
          }
        });

        const agentCallCounts = Array.from(agentCallsMap.entries()).map(([agent_id, data]) => ({
          agent_id,
          display_name: data.display_name,
          total_calls: data.total_calls
        }));

        // Calculate total duration in seconds
        const totalSeconds = calls.reduce((acc, call) => {
          return acc + parseInterval(call.call_duration);
        }, 0);

        // Calculate status stats
        const statusCounts = calls.reduce((acc: { [key: string]: number }, call) => {
          const status = call.call_successful ? 'Success' : 'Failed';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        const statusData = Object.entries(statusCounts).map(([name, value]) => ({
          name,
          value
        }));

        setAgentStats(agentCallCounts);
        setTotalDuration(totalSeconds);
        setStatusStats(statusData);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userEmail]);

  if (loading) {
    return <div className="p-8 text-center text-zinc-400">Loading dashboard data...</div>;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center gap-3 mb-8">
        <LayoutDashboard className="w-6 h-6 text-emerald-400" />
        <h2 className="text-2xl font-bold text-white">Dashboard</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-zinc-800/50 rounded-lg p-6 border border-zinc-700/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Phone className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-zinc-400">Total Calls</h3>
              <p className="text-2xl font-bold text-white">
                {agentStats.reduce((acc, agent) => acc + agent.total_calls, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-800/50 rounded-lg p-6 border border-zinc-700/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-zinc-400">Total Duration</h3>
              <p className="text-2xl font-bold text-white">{formatDuration(totalDuration)}</p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-800/50 rounded-lg p-6 border border-zinc-700/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <PieChart className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-zinc-400">Success Rate</h3>
              <p className="text-2xl font-bold text-white">
                {Math.round((statusStats.find(s => s.name === 'Success')?.value || 0) / 
                  statusStats.reduce((acc, stat) => acc + stat.value, 0) * 100)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-800/50 rounded-lg p-6 border border-zinc-700/50">
          <h3 className="text-lg font-semibold text-white mb-6">Calls by Agent</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th 
                    className="text-left py-3 px-4 text-zinc-400 font-medium cursor-pointer"
                    onClick={() => handleSort('display_name')}
                  >
                    <div className="flex items-center gap-2">
                      Agent Name
                      {sortConfig.key === 'display_name' && (
                        sortConfig.direction === 'desc' ? 
                          <ArrowDown className="w-4 h-4" /> : 
                          <ArrowUp className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-right py-3 px-4 text-zinc-400 font-medium cursor-pointer"
                    onClick={() => handleSort('total_calls')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      Total Calls
                      {sortConfig.key === 'total_calls' && (
                        sortConfig.direction === 'desc' ? 
                          <ArrowDown className="w-4 h-4" /> : 
                          <ArrowUp className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAgentStats.map((agent) => (
                  <tr 
                    key={agent.agent_id}
                    className="border-b border-zinc-700/50 hover:bg-zinc-700/20"
                  >
                    <td className="py-3 px-4 text-zinc-300">{agent.display_name}</td>
                    <td className="py-3 px-4 text-right text-emerald-400 font-medium">
                      {agent.total_calls}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-zinc-800/50 rounded-lg p-6 border border-zinc-700/50">
          <h3 className="text-lg font-semibold text-white mb-6">Call Status Distribution</h3>
          <div className="h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie
                  data={statusStats}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                >
                  {statusStats.map((entry) => (
                    <Cell 
                      key={`cell-${entry.name}`}
                      fill={STATUS_COLORS[entry.name as keyof typeof STATUS_COLORS]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#18181B',
                    border: '1px solid #374151',
                    borderRadius: '0.5rem'
                  }}
                  itemStyle={{ color: '#fff' }}
                />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}